import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import { Truck, Plus, Edit, Trash2, Eye, Search, X } from 'lucide-react';
import { toast } from 'sonner';

const API = `${window.location.origin}/api`;

const EmpurradoresPage = () => {
    const [empurradores, setEmpurradores] = useState([]);
    const [loading, setLoading] = useState(true);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [detailDialogOpen, setDetailDialogOpen] = useState(false);
    const [selectedEmpurrador, setSelectedEmpurrador] = useState(null);
    const [editingEmpurrador, setEditingEmpurrador] = useState(null);
    const [searchTerm, setSearchTerm] = useState('');
    const { hasRole } = useAuth();

    const [formData, setFormData] = useState({
        numero: '',
        nome: '',
        numero_inscricao: '',
        ab: '',
        porte_bruto: '',
        potencia: '',
        status: 'ativo',
        observacoes: ''
    });

    useEffect(() => {
        fetchEmpurradores();
    }, []);

    const fetchEmpurradores = async () => {
        try {
            const response = await axios.get(`${API}/empurradores`);
            setEmpurradores(response.data);
        } catch (error) {
            toast.error('Erro ao carregar empurradores');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        const data = {
            ...formData,
            ab: formData.ab ? parseFloat(formData.ab) : null,
            porte_bruto: formData.porte_bruto ? parseFloat(formData.porte_bruto) : null,
            potencia: formData.potencia ? parseFloat(formData.potencia) : null
        };
        
        try {
            if (editingEmpurrador) {
                await axios.put(`${API}/empurradores/${editingEmpurrador.id}`, data);
                toast.success('Empurrador atualizado!');
            } else {
                await axios.post(`${API}/empurradores`, data);
                toast.success('Empurrador cadastrado!');
            }
            setDialogOpen(false);
            resetForm();
            fetchEmpurradores();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao salvar empurrador');
        }
    };

    const handleEdit = (empurrador) => {
        setEditingEmpurrador(empurrador);
        setFormData({
            numero: empurrador.numero,
            nome: empurrador.nome,
            numero_inscricao: empurrador.numero_inscricao || '',
            ab: empurrador.ab?.toString() || '',
            porte_bruto: empurrador.porte_bruto?.toString() || '',
            potencia: empurrador.potencia?.toString() || '',
            status: empurrador.status,
            observacoes: empurrador.observacoes || ''
        });
        setDialogOpen(true);
    };

    const handleDelete = async (empurrador) => {
        if (!window.confirm(`Deseja remover o empurrador "${empurrador.nome}"?`)) return;
        
        try {
            await axios.delete(`${API}/empurradores/${empurrador.id}`);
            toast.success('Empurrador removido!');
            fetchEmpurradores();
        } catch (error) {
            toast.error(error.response?.data?.detail || 'Erro ao remover empurrador');
        }
    };

    const resetForm = () => {
        setFormData({
            numero: '',
            nome: '',
            numero_inscricao: '',
            ab: '',
            porte_bruto: '',
            potencia: '',
            status: 'ativo',
            observacoes: ''
        });
        setEditingEmpurrador(null);
    };

    const canEdit = hasRole(['admin', 'gestor']);
    const canDelete = hasRole(['admin']);

    // Filtrar empurradores com base na busca
    const filteredEmpurradores = empurradores.filter(emp => {
        if (!searchTerm) return true;
        const term = searchTerm.toLowerCase();
        return (
            emp.nome?.toLowerCase().includes(term) ||
            emp.numero?.toLowerCase().includes(term) ||
            emp.numero_inscricao?.toLowerCase().includes(term) ||
            emp.status?.toLowerCase().includes(term)
        );
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <div className="animate-pulse text-cyan-400">Carregando...</div>
            </div>
        );
    }

    return (
        <div className="space-y-6 animate-fade-in" data-testid="empurradores-page">
            {/* Header */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h1 className="font-heading text-2xl font-bold text-white tracking-tight">
                        Cadastro de Empurradores
                    </h1>
                    <p className="text-sm text-slate-400 mt-1">
                        Gerenciamento de embarcações empurradoras
                    </p>
                </div>

                <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 w-full sm:w-auto">
                    {/* Campo de Busca */}
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                        <Input
                            type="text"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Buscar empurradores..."
                            className="pl-10 pr-8 w-full sm:w-64 bg-slate-800/50 border-slate-700 text-white placeholder:text-slate-500 focus:border-cyan-500"
                            data-testid="search-empurradores"
                        />
                        {searchTerm && (
                            <button
                                onClick={() => setSearchTerm('')}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-white"
                                data-testid="clear-search-empurradores"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        )}
                    </div>
                
                    {canEdit && (
                        <Dialog open={dialogOpen} onOpenChange={(open) => {
                            setDialogOpen(open);
                            if (!open) resetForm();
                        }}>
                            <DialogTrigger asChild>
                                <Button 
                                    data-testid="btn-new-empurrador"
                                    className="bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm"
                                >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Novo Empurrador
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="bg-slate-900 border-slate-800 rounded-sm max-w-lg">
                                <DialogHeader>
                                    <DialogTitle className="font-heading text-xl text-white">
                                        {editingEmpurrador ? 'Editar Empurrador' : 'Novo Empurrador'}
                                    </DialogTitle>
                                </DialogHeader>
                            
                            <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            Nº do Empurrador *
                                        </Label>
                                        <Input
                                            data-testid="input-empurrador-numero"
                                            value={formData.numero}
                                            onChange={(e) => setFormData({...formData, numero: e.target.value})}
                                            required
                                            placeholder="Ex: EMP-001"
                                            className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            Nome *
                                        </Label>
                                        <Input
                                            data-testid="input-empurrador-nome"
                                            value={formData.nome}
                                            onChange={(e) => setFormData({...formData, nome: e.target.value})}
                                            required
                                            placeholder="Ex: ALMIRANTE FORTUNA"
                                            className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                        />
                                    </div>
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <div className="space-y-2">
                                        <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            Nº de Inscrição
                                        </Label>
                                        <Input
                                            value={formData.numero_inscricao}
                                            onChange={(e) => setFormData({...formData, numero_inscricao: e.target.value})}
                                            placeholder="Número de inscrição"
                                            className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            Status
                                        </Label>
                                        <Select 
                                            value={formData.status} 
                                            onValueChange={(value) => setFormData({...formData, status: value})}
                                        >
                                            <SelectTrigger className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm">
                                                <SelectValue />
                                            </SelectTrigger>
                                            <SelectContent className="bg-slate-900 border-slate-800">
                                                <SelectItem value="ativo">Ativo</SelectItem>
                                                <SelectItem value="inativo">Inativo</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>

                                <div className="grid grid-cols-3 gap-4">
                                    <div className="space-y-2">
                                        <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            AB (Arqueação Bruta)
                                        </Label>
                                        <Input
                                            type="number"
                                            step="0.01"
                                            value={formData.ab}
                                            onChange={(e) => setFormData({...formData, ab: e.target.value})}
                                            placeholder="0.00"
                                            className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            Porte Bruto (t)
                                        </Label>
                                        <Input
                                            type="number"
                                            step="0.01"
                                            value={formData.porte_bruto}
                                            onChange={(e) => setFormData({...formData, porte_bruto: e.target.value})}
                                            placeholder="0.00"
                                            className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                            Potência (HP)
                                        </Label>
                                        <Input
                                            type="number"
                                            step="0.01"
                                            value={formData.potencia}
                                            onChange={(e) => setFormData({...formData, potencia: e.target.value})}
                                            placeholder="0.00"
                                            className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm"
                                        />
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <Label className="text-slate-300 text-xs uppercase tracking-wider font-mono">
                                        Observações
                                    </Label>
                                    <Textarea
                                        value={formData.observacoes}
                                        onChange={(e) => setFormData({...formData, observacoes: e.target.value})}
                                        placeholder="Observações adicionais..."
                                        className="bg-slate-900/50 border-slate-700 text-slate-200 rounded-sm min-h-[60px]"
                                    />
                                </div>

                                <div className="flex gap-3 pt-4">
                                    <Button
                                        type="button"
                                        variant="outline"
                                        onClick={() => {
                                            setDialogOpen(false);
                                            resetForm();
                                        }}
                                        className="flex-1 border-slate-700 text-slate-300 hover:bg-slate-800 rounded-sm"
                                    >
                                        Cancelar
                                    </Button>
                                    <Button
                                        type="submit"
                                        data-testid="btn-save-empurrador"
                                        className="flex-1 bg-cyan-600 hover:bg-cyan-700 text-white rounded-sm"
                                    >
                                        {editingEmpurrador ? 'Atualizar' : 'Cadastrar'}
                                    </Button>
                                </div>
                            </form>
                        </DialogContent>
                    </Dialog>
                )}
                </div>
            </div>

            {/* Table */}
            <Card className="bg-slate-900/50 border-slate-800 rounded-sm">
                <CardContent className="p-0">
                    {filteredEmpurradores.length === 0 ? (
                        <div className="p-12 text-center">
                            <Truck className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                            <p className="text-slate-400">
                                {searchTerm ? 'Nenhum empurrador encontrado para a busca' : 'Nenhum empurrador cadastrado'}
                            </p>
                        </div>
                    ) : (
                        <Table className="table-naval">
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Número</TableHead>
                                    <TableHead>Nome</TableHead>
                                    <TableHead>Potência</TableHead>
                                    <TableHead>Porte Bruto</TableHead>
                                    <TableHead>Status</TableHead>
                                    <TableHead>Ações</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {filteredEmpurradores.map((emp) => (
                                    <TableRow key={emp.id} data-testid={`empurrador-row-${emp.id}`}>
                                        <TableCell className="font-mono text-cyan-400">
                                            {emp.numero}
                                        </TableCell>
                                        <TableCell className="text-white font-medium">
                                            {emp.nome}
                                        </TableCell>
                                        <TableCell className="text-slate-400">
                                            {emp.potencia ? `${emp.potencia} HP` : '-'}
                                        </TableCell>
                                        <TableCell className="text-slate-400">
                                            {emp.porte_bruto ? `${emp.porte_bruto} t` : '-'}
                                        </TableCell>
                                        <TableCell>
                                            <span className={`
                                                px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider rounded-sm
                                                ${emp.status === 'ativo' ? 'badge-success' : 'badge-danger'}
                                            `}>
                                                {emp.status}
                                            </span>
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex gap-2">
                                                <Button
                                                    variant="ghost"
                                                    size="sm"
                                                    onClick={() => {
                                                        setSelectedEmpurrador(emp);
                                                        setDetailDialogOpen(true);
                                                    }}
                                                    className="text-slate-400 hover:text-white hover:bg-slate-800"
                                                >
                                                    <Eye className="w-4 h-4" />
                                                </Button>
                                                {canEdit && (
                                                    <>
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => handleEdit(emp)}
                                                            className="text-slate-400 hover:text-white hover:bg-slate-800"
                                                        >
                                                            <Edit className="w-4 h-4" />
                                                        </Button>
                                                        {canDelete && (
                                                            <Button
                                                                variant="ghost"
                                                                size="sm"
                                                                onClick={() => handleDelete(emp)}
                                                                className="text-red-400 hover:text-red-300 hover:bg-slate-800"
                                                                data-testid={`btn-delete-empurrador-${emp.id}`}
                                                            >
                                                                <Trash2 className="w-4 h-4" />
                                                            </Button>
                                                        )}
                                                    </>
                                                )}
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>

            {/* Detail Dialog */}
            <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
                <DialogContent className="bg-slate-900 border-slate-800 rounded-sm max-w-lg">
                    <DialogHeader>
                        <DialogTitle className="font-heading text-xl text-white flex items-center gap-2">
                            <Truck className="w-5 h-5 text-cyan-400" />
                            {selectedEmpurrador?.nome}
                        </DialogTitle>
                    </DialogHeader>
                    
                    {selectedEmpurrador && (
                        <div className="space-y-4 mt-4">
                            <div className="grid grid-cols-2 gap-4">
                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase">Número</p>
                                    <p className="text-white mt-1">{selectedEmpurrador.numero}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase">Nº Inscrição</p>
                                    <p className="text-white mt-1">{selectedEmpurrador.numero_inscricao || '-'}</p>
                                </div>
                            </div>
                            <div className="grid grid-cols-3 gap-4">
                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase">AB</p>
                                    <p className="text-white mt-1">{selectedEmpurrador.ab || '-'}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase">Porte Bruto</p>
                                    <p className="text-white mt-1">{selectedEmpurrador.porte_bruto ? `${selectedEmpurrador.porte_bruto} t` : '-'}</p>
                                </div>
                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase">Potência</p>
                                    <p className="text-white mt-1">{selectedEmpurrador.potencia ? `${selectedEmpurrador.potencia} HP` : '-'}</p>
                                </div>
                            </div>
                            {selectedEmpurrador.observacoes && (
                                <div>
                                    <p className="text-xs text-slate-500 font-mono uppercase">Observações</p>
                                    <p className="text-slate-300 mt-1 text-sm">{selectedEmpurrador.observacoes}</p>
                                </div>
                            )}
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default EmpurradoresPage;
